import { useState } from "react";
import {
  ArrowDown2,
  Book1,
  Calendar as CalendarIcon,
  Teacher,
} from "iconsax-reactjs";
import { cn } from "../../../../lib/utils";
import { toPersianDigits } from "../../../../utils/persianDigits";
import { getGradeLabel } from "../../../../constants/education";
import WizardStepHeader from "./WizardStepHeader";
import { formatChainDateRange, getSelectedSuggestion } from "./wizardUtils";
import type { ChainFormState } from "./wizardTypes";

type Props = {
  state: ChainFormState;
  lessonLabel: string;
};

export default function StepPreview({ state, lessonLabel }: Props) {
  const [expandedDay, setExpandedDay] = useState<number | null>(null);

  const toggleDay = (weekDay: number) =>
    setExpandedDay((current) => (current === weekDay ? null : weekDay));

  return (
    <div className="flex flex-col gap-6 " dir="rtl">
      <WizardStepHeader title="پیش‌نمایش زنجیره" />

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3 mb-6 max-w-[950px]">
        <SummaryChip
          icon={<Teacher size={18} />}
          label="پایه"
          value={getGradeLabel(state.grade)}
        />
        <SummaryChip
          icon={<Book1 size={18} />}
          label="رشته"
          value={lessonLabel || "-"}
        />
        <SummaryChip
          icon={<CalendarIcon size={18} />}
          label="تاریخ اجرا"
          value={formatChainDateRange(state.dateRange) || "-"}
        />
      </div>

      <div className="flex flex-col gap-3">
        {state.days.map((day, index) => {
          const suggestion = getSelectedSuggestion(day);
          const isExpanded = expandedDay === day.weekDay;

          return (
            <div key={day.weekDay} className="flex items-center gap-x-2">
              <span
                className={cn(
                  "flex size-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                  day.isGolden
                    ? "bg-warningVar-600 text-warningVar-light"
                    : "bg-primaryVar-300 text-primaryVar-50",
                )}
              >
                {toPersianDigits(index + 1)}
              </span>
              <div className="min-w-0 flex-1 rounded-xl border border-primaryVar-100">
                <button
                  type="button"
                  onClick={() => toggleDay(day.weekDay)}
                  className="flex w-full items-start justify-between gap-4 p-2 text-right"
                >
                  <span className="flex-1">
                    <span
                      className={cn(
                        "block text-[13px] font-medium",
                        day.isGolden
                          ? "text-warningVar-500"
                          : "text-primaryVar-400",
                      )}
                    >
                      {day.label}
                      {day.isGolden && " (چالش طلایی)"}
                    </span>
                    <span className="mt-1 block text-[11px] font-medium text-gray-500">
                      {suggestion?.content ??
                        "سوالی برای این روز انتخاب نشده است."}
                    </span>
                  </span>

                  <ArrowDown2
                    size={18}
                    className={cn(
                      "mt-1 shrink-0 text-gray-400 transition-transform",
                      isExpanded && "rotate-180",
                    )}
                  />
                </button>

                {isExpanded && suggestion ? (
                  <div className="flex flex-col gap-2 border-t border-gray-100 p-4">
                    {suggestion.options.map((option, optionIndex) => (
                      <div
                        key={optionIndex}
                        className="flex h-10 items-center rounded-lg border border-gray-200 px-4 text-sm text-gray-600"
                      >
                        {option.content ?? option.text ?? ""}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SummaryChip = ({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) => (
  <div className="flex h-14 items-center gap-x-4 rounded-xl border border-gray-200 px-4">
    <span className="text-gray-400">{icon}</span>
    <span className="text-right">
      <span className="block text-xs font-medium text-gray-400">{label}</span>
      <span className="block text-[13px] font-medium text-primaryVar-500">
        {value}
      </span>
    </span>
  </div>
);
